import moment from 'moment'
import { Base64 } from 'js-base64'

import * as JManError from './JManError'
import { Entity, User } from './Entity'
import { EntityCache } from './EntityCache'
import { AuthCache } from './AuthCache'

const uuidrx = /^[a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12}$/;

class JManAdd {

	constructor (parent) {
		this.parent = parent;
	}

	async entity (obj) {

		const res = await this.parent.fetch(`${this.parent.api}/${obj.type}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json"
			},
			body: JSON.stringify(obj)
		});

		if (res.ok) {
			return Entity.make(await res.json(), this.parent);
		} else {
			console.error("HTTP response", res);
			if (res.status == 400) {
				const body = await res.json();
				if (body.type && /JMan.*Error/.test(body.type)) {
					throw new JManError.JManErrorServerError(body);
				}
			}

			throw new JManError.JManError("Save entity failed");
		}
	}

	async triple (obj) {
		const res = await this.parent.fetch(`${this.parent.api}/triple`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json"
			},
			body: JSON.stringify(obj)
		});

		if (res.ok) {
			return await res.json();
		} else {
			console.error("HTTP response", res);
			throw new JManError.JManError("Save triple failed");
		}
	}
}

class JManReplace {

	constructor (parent) {
		this.parent = parent;
	}

	async entity (obj) {
		const res = await this.parent.fetch(`${this.parent.api}/${obj.id}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json"
			},
			body: JSON.stringify(obj)
		});

		if (res.ok) {
			return Entity.make(await res.json(), this.parent);
		} else {
			console.error("HTTP response", res);
			if (res.status == 400) {
				const body = await res.json();
				if (body.type && /JMan.*Error/.test(body.type)) {
					throw new JManError.JManErrorServerError(body);
				}
			}

			throw new JManError.JManError("Replace entity failed");
		}
	}
}

class JManPatch {

	constructor (parent) {
		this.parent = parent;
	}

	async entity (obj, changes) {
		const res = await this.parent.fetch(`${this.parent.api}/${obj.id}`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json"
			},
			body: JSON.stringify(changes)
		});

		if (res.ok) {
			return Entity.make(await res.json(), this.parent);
		} else {
			console.error("HTTP response", res);
			throw new JManError.JManError("Patch entity failed");
		}
	}
}

class JManDelete {

	constructor (parent) {
		this.parent = parent;
	}

	async entity (obj) {
// 		console.log("DELETE", `${this.parent.api}/${obj.id}`);
		const res = await this.parent.fetch(`${this.parent.api}/${obj.id}`, {
			method: "DELETE"
		});

		if (res.ok) {
			return obj;
		} else if (res.status == 404) {
			// Already deleted, proceed as normal
			return obj;
		} else {
			console.error("HTTP response", res);
			throw new JManError.JManError("Delete entity failed");
		}
	}

	async triple (obj) {
		// FIXME — Use proper URL functions
		const res = await this.parent.fetch(`${this.parent.api}/triple?s=${obj.subject}&o=${obj.object}&p=${obj.predicate}`, {
			method: "DELETE"
		});

		if (res.ok) {
			return obj;
		} else if (res.status == 404) {
			// Already deleted, proceed as normal
			return obj;
		} else {
			console.error("HTTP response", res);
			throw new JManError.JManError("Delete triple failed");
		}
	}
}

class JManSecrets {

	constructor (parent) {
		this.parent = parent;
	}

	async get (entity) {
		const uuid = entity.id;

		if (!entity.private) {
			entity.private = {};
		}

		const res = await this.parent.fetch(`${this.parent.api}/${uuid}/private/secrets`);
		if (res.ok) {
			const data = await res.json();
			if (entity.updatedOn != data.updatedOn) {
				throw JManError.JManError("Update conflict");
			}
			entity.private.secrets = data.secrets;
		} else {
			entity.private.secrets = [];
		}

		console.log("entity with secrets", JSON.parse(JSON.stringify(entity)));
		return entity;
	}

	async set (entity) {
		const uuid = entity.id;

		const res = await this.parent.fetch(`${this.parent.api}/${uuid}/private/secrets`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json"
			},
			body: JSON.stringify({
				secrets: entity.private.secrets,
				updatedOn: entity.updatedOn
			})
		});

		if (res.ok) {
			return await this.get(entity);
		} else {
			console.error("HTTP response", res);
			throw new JManError.JManError("Set secrets failed");
		}
	}
}

export class JMan {
	constructor ({uri, credentials = {}, jwt} = {}) {
		Object.defineProperty(this, 'api', {value: uri}); // Make non-enumerable
		Object.defineProperty(this, 'credentials', {value: credentials, writable: true});
		Object.defineProperty(this, 'cache', {value: new EntityCache});
		Object.defineProperty(this, 'auth', {value: new AuthCache});
		Object.defineProperty(this, 'listeners', { value: {}, writable: true});

		this.jwt = jwt;

		this.add = new JManAdd(this);
		this.replace = new JManReplace(this);
		this.patch = new JManPatch(this);
		this.delete = new JManDelete(this);
		this.secrets = new JManSecrets(this);
	}

	on (event, callback) {
		if (!this.listeners[event]) {
			this.listeners[event] = [];
		}

		if (!this.listeners[event].find(l => l === callback)) {
			this.listeners[event].push(callback);
		}
	}

	removeListener (event, callback) {
		if (this.listeners[event]) {
			let index = this.listeners[event].findIndex(item => item === callback);
			if (index != -1) {
				this.listeners[event].splice(index, 1);
			}
		}
	}

	emit (event) {
		return Promise.resolve().then( () => {
			if (this.listeners[event]) {
				const args = [...arguments].splice(1);
				for (const callback of this.listeners[event]) {
					callback(...args);
				}
			}
		});
	}

	setCredentials (credentials) {
		this.credentials = credentials
	}

	async setJWT (jwt) {
		this.jwt = jwt;
		this.user = jwt ?
			new User(JSON.parse(Base64.decode(jwt.split(".")[1])), this) :
			null;
		this.auth.putJwt(jwt); // Save to persistent cache
		try {
			this.user && await this.user.expand();
		} catch (err) {
			console.warn("Failed to expand user. Possibly offline?", err);
		}
	}

	async login (credentials = {}) {

		this.logout();

		if (credentials.user) {

			const authorization = Base64.encode([
				credentials.user,
				credentials.password
			].join(":"));

			const res = await fetch(`${this.api}/auth/token`, {
				headers: {
					"Authorization": `Basic ${authorization}`
				}
			});

			if (res.ok) {
				let jwt = await res.json();
				await this.setJWT(jwt);
				return this.jwt;
			} else {
				throw new JManError.JManErrorLoginFailed();
			}

		} else {
			throw new JManError.JManErrorLoginRequired();
		}
	}

	logout () {
		this.jwt = undefined;
		this.auth.clearJwt();
	}

	/**
	 * Check if we are logged in
	 *
	 * This method polls the API to check if we
	 * (still) have a valid token. As a side effect
	 * it also checks that the API is reachable.
	 *
	 * It does so by polling the root of the API,
	 * which should return a 204 (No content) if
	 * we are logged in, and a 401 (Unauthorized)
	 * if we are not.
	 *
	 * @returns true if we have successfully
	 * validated our token and false if:
	 * - we do not have a token;
	 * - the token is not valid;
	 * - the API is not reachable.
	 */
	async status () {
		const res = await this.fetch(`${this.api}/`);
		if (res.ok) {
			return true;
		} else {
			return false;
		}
	}

	async fetch (...args) {

		try {

			if (this.jwt) {
				args[1] = Object.assign({
					headers: {}
				}, args[1]);
				args[1].headers['Authorization'] = `Bearer ${this.jwt}`;
			}

			const res = await fetch(...args);
			if (res.ok) {
				if (res.headers.get("X-JMan-Auth-Token")) {
					// Refresh authentication token, the case being
					this.setJWT(res.headers.get("X-JMan-Auth-Token"));
				}
				return res;
			} else {
				if (res.status == 401) {
	// 				console.log("Need to authenticate");
					await this.login();
					let res = await this.fetch(...args);
					return res;
				} else {
	// 				throw new JManError.JManError(`API request error (status: ${res.status})`);
					console.log("RES", res);
					throw new JManError.JManErrorNetworkError();
					return res;
				}
			}

		} catch (err) {
			throw err instanceof TypeError
				? new JManError.JManErrorNetworkError() // Assume that we are offline
				: err;
		}

	}

	async triple (subject, object, predicate) {
		let url = new URL(`${this.api}/triple`);
		subject && url.searchParams.set("s", subject);
		object && url.searchParams.set("o", object);
		predicate && url.searchParams.set("p", predicate);
		const res = await this.fetch(url.toString());
		const rows = await res.json();

		return rows.map(row => {
			return {
				subject: row.subject,
				object: row.object,
				predicate: row.predicate
			}
		});
	}

	async entityByUuid (uuid, opts = {}) {
		const res = await this.fetch(`${this.api}/${uuid}`);
		if (res.ok) {
			const entity = await res.json();

			if (opts.secrets) {
				await this.secrets.get(entity);
			}

			return Entity.make(entity, this);
		} else {
			return uuid;
		}
	}

	async entities ({type="", modifiedSince, offset=0, limit=1000, fields="", cache=false, raw=false} = {}) {
		if (modifiedSince) {
			// We need to pass this argument to milliseconds precision
			modifiedSince = moment(modifiedSince).valueOf();
		}

		const res = await this.fetch(`${this.api}/${type}?modifiedSince=${modifiedSince||""}&offset=${offset}&limit=${limit}&fields=${fields}`);
		const rows = await res.json();

		if (cache) {
			for (const row of rows) {
				await this.cache.put(row);
			}
		}

		this.emit('entities', rows, ...arguments);

		return raw
			? rows
			: rows.map( row => Entity.make(row, this) );
	}

};



// WEBPACK FOOTER //
// ./src/assets/js/JMan/JMan.js